/**
 * Reports device connectivity to TanStack Query's onlineManager via
 * @capacitor/network. navigator.onLine is unreliable inside a Capacitor
 * WebView, so without this offline guards and refetch-on-reconnect cannot
 * trust the browser's online flag.
 *
 * Network.getStatus() is asynchronous (a native round-trip). The device's real
 * status must be applied to onlineManager BEFORE any consumer relies on
 * connectivity (initializeAppRQ gates the offline path on
 * onlineManager.isOnline()). To make that ordering possible, this module
 * exposes awaitOnlineStatus(), which resolves only after the first getStatus()
 * has been applied. Consumers await it so they never read a stale default.
 */
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Network } from "@capacitor/network";
import { onlineManager } from "@tanstack/react-query";

let initialized = false;

// Resolves once Network.getStatus() has been applied to onlineManager. Shared
// between initOnlineManagerBridge() (App.tsx useEffect) and any consumer that
// awaits awaitOnlineStatus(), so getStatus() runs at most once.
let statusReady: Promise<void> | undefined;

function applyDeviceConnected(connected: boolean): void {
  onlineManager.setOnline(connected);
}

function resolveInitialStatus(): Promise<void> {
  if (!statusReady) {
    statusReady = Network.getStatus()
      .then((status) => {
        applyDeviceConnected(status.connected);
      })
      .catch(() => {
        // Leave onlineManager at its current value if the native call fails.
      });
  }
  return statusReady;
}

/**
 * Read the device's current connectivity from Capacitor and apply it to
 * onlineManager. Used on foreground entry, when the networkStatusChange
 * listener may have been missed while the WebView was suspended.
 */
async function syncDeviceConnectivity(): Promise<void> {
  try {
    const status = await Network.getStatus();
    applyDeviceConnected(status.connected);
  } catch {
    // Leave onlineManager at its current value if the native call fails.
  }
}

/**
 * Start reporting device connectivity. Kicks off the one-time real-status
 * resolution and registers the reconnect listener. On native platforms also
 * re-syncs on foreground entry, since networkStatusChange events can be
 * missed while the WebView is suspended. Idempotent.
 */
export function initOnlineManagerBridge(): void {
  if (initialized) return;
  initialized = true;

  void resolveInitialStatus();

  void Network.addListener("networkStatusChange", (status) => {
    applyDeviceConnected(status.connected);
  });

  if (Capacitor.isNativePlatform()) {
    void App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void syncDeviceConnectivity();
      }
    });
  }
}

/**
 * Resolves after the device's real connectivity (from Network.getStatus()) has
 * been applied to onlineManager. Callers that must not read a stale default
 * (initializeAppRQ) await this first.
 * Triggers the resolution if initOnlineManagerBridge() has not run yet.
 */
export function awaitOnlineStatus(): Promise<void> {
  return resolveInitialStatus();
}

/** Test helper — reset module state between tests. */
export function resetOnlineManagerBridgeForTests(): void {
  initialized = false;
  statusReady = undefined;
}
